/**
 * Phạm vi của một dòng IP/CIDR: ô chọn "Một địa chỉ / Dải /24 / Dải /16" cạnh
 * ô nhập, chữ phụ "113.185.44.0 – 113.185.44.255 · 256 địa chỉ" trong bảng, và
 * gợi ý khi người dùng gõ địa chỉ đuôi `.0` mà phạm vi vẫn là một địa chỉ.
 *
 * Vì sao có tệp này: gõ `113.185.44.0` thì backend lưu `113.185.44.0/32` (đúng
 * một địa chỉ), trong khi người gõ thường nghĩ mình vừa chặn cả dải. Giao diện
 * nói rõ phạm vi **trước khi** thêm dòng, và cho chọn dải mà không phải biết cú
 * pháp CIDR.
 *
 * Dùng chung cho danh sách IP của tenant (`BangCidr`) và blocklist nền tảng
 * (`BlocklistNenTang`). Hàm thuần, không dựng chuỗi hiển thị; luật chuẩn hoá
 * vẫn là `chuanHoaCidr`, backend vẫn là chốt chặn.
 */
import { chuanHoaCidr, docIpv4, docIpv6, mang4, matNa6, vietIpv4, vietIpv6 } from './cidr.ts';

/** Mức phạm vi trên ô chọn, độc lập với phiên bản IP: đổi IPv4 ↔ IPv6 vẫn giữ mức. */
export type MucPhamVi = 'mot' | 'hep' | 'rong';

export const MUC_PHAM_VI: readonly MucPhamVi[] = ['mot', 'hep', 'rong'];

/** Tiền tố ứng với từng mức: IPv4 /32 · /24 · /16, IPv6 /128 · /64 · /48. */
export const TIEN_TO_THEO_MUC: Record<4 | 6, Record<MucPhamVi, number>> = {
  4: { mot: 32, hep: 24, rong: 16 },
  6: { mot: 128, hep: 64, rong: 48 },
};

export type KetQuaNhap =
  | { ok: false; loi: 'dinh_dang' | 'tien_to' }
  | {
      ok: true;
      /** Chuỗi sẽ gửi backend, đã chuẩn hoá. */
      cidr: string;
      phienBan: 4 | 6;
      tienTo: number;
      /** Người dùng tự gõ `/xx`: ô chọn phạm vi không quyết nữa. */
      tuGoTienTo: boolean;
      /** Mức của ô chọn khớp với tiền tố cuối cùng; `null` khi tự gõ một tiền tố ngoài ba mức. */
      muc: MucPhamVi | null;
      /** Địa chỉ gõ vào không phải đầu dải nên đã được đưa về địa chỉ mạng. */
      daDuaVeDauDai: boolean;
    };

function mucCuaTienTo(phienBan: 4 | 6, tienTo: number): MucPhamVi | null {
  return MUC_PHAM_VI.find((m) => TIEN_TO_THEO_MUC[phienBan][m] === tienTo) ?? null;
}

function phanIp(cidr: string): string {
  const i = cidr.indexOf('/');
  return i < 0 ? cidr : cidr.slice(0, i);
}

/**
 * Ghép ô nhập với ô chọn phạm vi thành CIDR sẽ lưu.
 *
 * - Gõ sẵn `/xx` thì dùng đúng tiền tố đó, bỏ qua `muc`.
 * - IP trần thì lấy tiền tố theo `muc`, rồi đưa về địa chỉ mạng
 *   (`113.185.44.135` + `hep` → `113.185.44.0/24`).
 *
 * Chuỗi rỗng là `null`: ô trống không phải lỗi định dạng.
 */
export function ghepCidr(dauVao: string, muc: MucPhamVi): KetQuaNhap | null {
  const s = dauVao.trim();
  if (!s) return null;

  if (s.includes('/')) {
    const kq = chuanHoaCidr(s);
    if (!kq.ok) return kq;
    const ipGo = chuanHoaCidr(phanIp(s));
    return {
      ok: true,
      cidr: kq.cidr,
      phienBan: kq.phienBan,
      tienTo: kq.tienTo,
      tuGoTienTo: true,
      muc: mucCuaTienTo(kq.phienBan, kq.tienTo),
      daDuaVeDauDai: ipGo.ok && phanIp(ipGo.cidr) !== phanIp(kq.cidr),
    };
  }

  const tran = chuanHoaCidr(s);
  if (!tran.ok) return tran;
  const tienTo = TIEN_TO_THEO_MUC[tran.phienBan][muc];
  const kq = chuanHoaCidr(`${phanIp(tran.cidr)}/${tienTo}`);
  if (!kq.ok) return kq;
  return {
    ok: true,
    cidr: kq.cidr,
    phienBan: kq.phienBan,
    tienTo,
    tuGoTienTo: false,
    muc,
    daDuaVeDauDai: phanIp(tran.cidr) !== phanIp(kq.cidr),
  };
}

export type PhamViCidr =
  | { loai: 'mot' }
  /** Dải đếm được: đầu, cuối, số địa chỉ (IPv4 mọi dải; IPv6 tới /112). */
  | { loai: 'dai'; dau: string; cuoi: string; soDiaChi: number }
  /** Dải IPv6 rộng: số địa chỉ quá lớn để đọc, chỉ nói tiền tố. */
  | { loai: 'daiLon'; tienTo: number };

/** Dải IPv6 rộng hơn /112 (65.536 địa chỉ) thì không liệt kê đầu–cuối. */
const TIEN_TO_V6_DEM_DUOC = 112;

/** Phạm vi của một CIDR đã lưu, để hiện chữ phụ trong bảng. Chuỗi hỏng thì `null`. */
export function phamViCidr(cidr: string): PhamViCidr | null {
  const kq = chuanHoaCidr(cidr);
  if (!kq.ok) return null;
  const ip = phanIp(kq.cidr);

  if (kq.phienBan === 4) {
    if (kq.tienTo === 32) return { loai: 'mot' };
    const soDiaChi = 2 ** (32 - kq.tienTo);
    const dau = mang4(docIpv4(ip)!, kq.tienTo);
    return { loai: 'dai', dau: vietIpv4(dau), cuoi: vietIpv4(dau + soDiaChi - 1), soDiaChi };
  }

  if (kq.tienTo === 128) return { loai: 'mot' };
  if (kq.tienTo < TIEN_TO_V6_DEM_DUOC) return { loai: 'daiLon', tienTo: kq.tienTo };
  const dau = docIpv6(ip)!;
  const cuoi = dau | (((1n << 128n) - 1n) ^ matNa6(kq.tienTo));
  return { loai: 'dai', dau: vietIpv6(dau), cuoi: vietIpv6(cuoi), soDiaChi: 2 ** (128 - kq.tienTo) };
}

/**
 * Gợi ý khi gõ IPv4 đuôi `.0` mà phạm vi vẫn là một địa chỉ: địa chỉ đó thường
 * là đầu một dải, người gõ nhiều khả năng muốn cả dải. Đuôi `.0.0` gợi ý /16,
 * đuôi `.0` gợi ý /24. Không chặn gì — chỉ đề nghị.
 *
 * Không gợi ý khi đã gõ `/xx`, khi đã chọn dải, hay với `0.0.0.0`.
 */
export function goiYDuoiKhong(dauVao: string, muc: MucPhamVi): { muc: MucPhamVi; cidr: string } | null {
  const s = dauVao.trim();
  if (muc !== 'mot' || s.includes('/')) return null;
  const ip = docIpv4(s);
  if (ip === null || ip === 0) return null;

  const goiY: MucPhamVi | null = ip % 65536 === 0 ? 'rong' : ip % 256 === 0 ? 'hep' : null;
  if (!goiY) return null;
  const kq = ghepCidr(s, goiY);
  return kq?.ok ? { muc: goiY, cidr: kq.cidr } : null;
}
